import axios from 'axios';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { api } from './Api';
import { Store } from './Store';

// One PIN unlock for the whole developer area.
//
// Previously every developer page asked for the PIN on its own. Now a single
// unlock issues a short-lived ticket from the server, held in memory here and
// sent as x-dev-ticket by every developer screen. It is deliberately NOT put in
// localStorage: this is the most privileged area in the app, so a reload should
// cost one PIN entry rather than leave a standing key on disk.

const DeveloperAccessContext = createContext(null);

export function DeveloperAccessProvider({ children }) {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [ticket, setTicket] = useState('');
  const [checking, setChecking] = useState(false);

  const unlock = useCallback(
    async (pin) => {
      setChecking(true);
      try {
        const { data } = await axios.post(
          api + '/api/developer/pin/verify',
          { pin },
          { headers: { Authorization: `Bearer ${userInfo?.token}` } }
        );
        if (!data.ok) return { ok: false, message: 'Wrong developer PIN.' };
        setTicket(data.ticket || '');
        return { ok: true };
      } catch (err) {
        return { ok: false, message: err?.response?.data?.message || 'Could not verify the PIN.' };
      } finally {
        setChecking(false);
      }
    },
    [userInfo]
  );

  const lock = useCallback(() => setTicket(''), []);

  // Headers for any developer endpoint: admin token plus the unlock ticket.
  const devHeaders = useMemo(
    () => ({ Authorization: `Bearer ${userInfo?.token}`, 'x-dev-ticket': ticket }),
    [userInfo, ticket]
  );

  const value = useMemo(
    () => ({ unlocked: Boolean(ticket), checking, unlock, lock, devHeaders, ticket }),
    [ticket, checking, unlock, lock, devHeaders]
  );

  return (
    <DeveloperAccessContext.Provider value={value}>{children}</DeveloperAccessContext.Provider>
  );
}

export function useDeveloperAccess() {
  const ctx = useContext(DeveloperAccessContext);
  if (!ctx) throw new Error('useDeveloperAccess must be used inside DeveloperAccessProvider');
  return ctx;
}
